import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col, Form, Spinner, ButtonGroup, Button } from "react-bootstrap";
import AddNewModal from "./crudModals/AddNewModal";
import RolesCard from "./AnnouncementsCard";

const AnnouncementsComponent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(""); // State to manage grouping
  const { user: authUser } = useSelector((x) => x.auth);
  const url = process.env.REACT_APP_URL;

  const getAnnouncements = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${url}/announcements`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      setAnnouncements(data);
    } catch (e) {
      console.log(`Announcements component - Get Announcements error: ${e}`);
    }
    setIsLoading(false);
  }, [url]);

  useEffect(() => {
    getAnnouncements();
  }, [getAnnouncements]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleGroupChange = (group) => {
    setSelectedGroup(group);
  };

  // Filter announcements based on user access level
  const filteredAnnouncements = announcements.filter((announcement) => {
    if (!authUser) {
      // If no authenticated user, show only public announcements
      return announcement.to === "Public";
    }
    if (announcement.to === "Officer" && authUser.user.acl !== "Officer") {
      return false;
    }
    if (announcement.to === "Member" && !["Officer", "Member"].includes(authUser.user.acl)) {
      return false;
    }
    if (announcement.to === "Guest" && !["Officer", "Member", "Guest"].includes(authUser.user.acl)) {
      return false;
    }
    return true;
  });

  // Further filter announcements based on search term
  const searchedAnnouncements = filteredAnnouncements.filter((announcement) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      announcement.title.toLowerCase().includes(searchTermLower) ||
      (announcement.shortDescription && announcement.shortDescription.toLowerCase().includes(searchTermLower)) ||
      (announcement.description && announcement.description.toLowerCase().includes(searchTermLower))
    );
  });

  // Group announcements based on the selected group
  const groupedAnnouncements = selectedGroup
    ? searchedAnnouncements.filter((announcement) => announcement.to === selectedGroup)
    : searchedAnnouncements;

  if (isLoading) {
    return <Spinner animation="border" variant="danger" />;
  } else {
    return (
      <Container>
        <Row className="header mb-2">
          <Col xs={6} lg={6} className="title">
            <h3>Announcements</h3>
          </Col>
          {authUser && authUser.user.acl === "Officer" && (
            <Col xs={6} lg={6} key={"newAnnouncement"}>
              <AddNewModal getAnnouncements={getAnnouncements} />
            </Col>
          )}
        </Row>
        <Row className="mb-2">
          <Col xs={12} lg={12}>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </Col>
        </Row>
        {authUser && (
          <Row className="mb-2">
            <Col xs={12} lg={12}>
              <ButtonGroup>
                {authUser.user.acl === "Officer" && (
                  <Button variant="outline-primary" onClick={() => handleGroupChange("Officer")}>
                    Officers
                  </Button>
                )}
                {["Officer", "Member"].includes(authUser.user.acl) && (
                  <Button variant="outline-primary" onClick={() => handleGroupChange("Member")}>
                    Members
                  </Button>
                )}
                {["Officer", "Member", "Guest"].includes(authUser.user.acl) && (
                  <Button variant="outline-primary" onClick={() => handleGroupChange("Guest")}>
                    Guests
                  </Button>
                )}
                <Button variant="outline-primary" onClick={() => handleGroupChange("Public")}>
                  Public
                </Button>
                <Button variant="outline-secondary" onClick={() => handleGroupChange("")}>
                  All
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        )}
        <Row className="mb-2 mt-2">
          {groupedAnnouncements.length > 0 ? (
            groupedAnnouncements.map((announcement) => (
              <Col xs={12} lg={12} key={announcement._id}>
                <RolesCard
                  announcement={announcement}
                  getAnnouncements={getAnnouncements}
                />
              </Col>
            ))
          ) : (
            <Col>
              <p className="text-center">No announcements available</p>
            </Col>
          )}
        </Row>
      </Container>
    );
  }
};

export default AnnouncementsComponent;